import { challengeHint, markerFits, type ChallengeSpot } from "./challenge";
import type { InputFrame } from "./input";
import { TIER_NAMES, type Rung, type Tier } from "./ladder";
import type { Level } from "./level";
import { rad } from "./prims/types";

// Challenge hubs (#86): instead of a marker per challenge scattered around the
// park, a map has one hub per tier. Roll into a hub, press △ and a list of
// that tier's challenges opens; pick one and it starts at its own start spot
// (where its marker used to be, `challengeSpots`). A tier's hub stays locked
// until every challenge on the tier below is done, so unlocking one sends you
// somewhere new. Sim-free and three.js-free: Game feeds it the skater's spot
// and the ladder; `render/markers.ts` draws the hubs, `ui/HubList.tsx` the list.

/** A map file's `hubs` entry: where a tier's hub stands; `yaw` in degrees, which way a skater sent there faces. */
export interface MapHub {
  tier: Tier;
  x: number;
  z: number;
  yaw: number;
}

/** A hub in the park. */
export interface HubSpot {
  /** "hub-1", "hub-2", "hub-3". */
  id: string;
  tier: Tier;
  x: number;
  z: number;
  /** Radians, like `Level.spawn.yaw`. */
  yaw: number;
}

/** How close (horizontally) the skater has to be to a hub to open it: bigger than a challenge marker. */
export const HUB_R = 2.2;
/** Flat, rail-free ground a hub needs around it. */
export const HUB_CLEAR = 2.6;
/** Closest two hubs (or a hub and the spawn) may be. */
export const HUB_SPACING = 8;

/** A map file's hubs as the park's, in tier order. */
export function mapHubs(hubs: readonly MapHub[] | undefined): HubSpot[] {
  return [...(hubs ?? [])]
    .sort((a, b) => a.tier - b.tier)
    .map((h) => ({ id: hubId(h.tier), tier: h.tier, x: h.x, z: h.z, yaw: rad(h.yaw) }));
}

export function hubId(tier: Tier): string {
  return `hub-${tier}`;
}

/**
 * A hub's sign (#87 part 3): an overhead gantry, two posts either side of the
 * ring (across the way a skater sent there faces, so you roll through it)
 * holding a banner above the hub's badge. It's scenery, not in the
 * heightfield: the sim and `playcheck` never see it, so it can't block
 * anything, and the posts stand on the hub's own cleared ground.
 */
export const HUB_SIGN = {
  /** Post centers, this far either side of the hub's center. */
  postOffset: 2.45,
  /** Post half-width. */
  postR: 0.08,
  /** The banner's bottom and top edges (m above the ground): above the hub's badge and light column. */
  bannerBottom: 4.7,
  bannerTop: 5.9,
} as const;

/** Where a hub's sign posts stand: either side of the ring, across its yaw. */
export function hubSignPosts(h: HubSpot): [{ x: number; z: number }, { x: number; z: number }] {
  // A skater sent there faces (sin yaw, cos yaw); across that is (cos yaw, -sin yaw).
  const ax = Math.cos(h.yaw) * HUB_SIGN.postOffset;
  const az = -Math.sin(h.yaw) * HUB_SIGN.postOffset;
  return [
    { x: h.x - ax, z: h.z - az },
    { x: h.x + ax, z: h.z + az },
  ];
}

/** What's wrong with where a map's hubs stand, one line each: off flat, clear ground, or too close together or to the spawn. */
export function checkHubPlaces(hubs: readonly HubSpot[], level: Level): string[] {
  const problems: string[] = [];
  hubs.forEach((h, i) => {
    const others = [level.spawn, ...hubs.slice(0, i)];
    if (!markerFits(level, h.x, h.z, [], HUB_CLEAR))
      problems.push(`${h.id} at (${h.x}, ${h.z}): needs flat, rail-free ground ${HUB_CLEAR} m around it, inside the fence`);
    else if (others.some((o) => Math.hypot(o.x - h.x, o.z - h.z) < (o === level.spawn ? HUB_R + 1 : HUB_SPACING)))
      problems.push(`${h.id} at (${h.x}, ${h.z}): too close to the spawn or another hub`);
    else if (hubSignPosts(h).some((p) => !markerFits(level, p.x, p.z, [], HUB_SIGN.postR * 2)))
      problems.push(`${h.id} at (${h.x}, ${h.z}): its sign's posts need flat, rail-free ground`);
  });
  return problems;
}

/** The hub the skater at (x, z) is standing in, if any. */
export function hubAt(hubs: readonly HubSpot[], x: number, z: number): HubSpot | null {
  let best: HubSpot | null = null;
  let bestD = HUB_R;
  for (const h of hubs) {
    const d = Math.hypot(h.x - x, h.z - z);
    if (d <= bestD) {
      best = h;
      bestD = d;
    }
  }
  return best;
}

/** The goal ids on a tier, in ladder order. */
export function tierGoals(rungs: readonly Rung[], tier: Tier): string[] {
  return rungs.filter((r) => r.tier === tier).map((r) => r.id);
}

/** Challenges on the tier below that still aren't done: what a tier's hub waits on (none for tier 1). */
export function hubMissing(rungs: readonly Rung[], tier: Tier, done: (id: string) => boolean): string[] {
  return tier === 1 ? [] : tierGoals(rungs, (tier - 1) as Tier).filter((id) => !done(id));
}

/** A tier's hub opens once every challenge on the tier below is done (all of them, THPS 4 style: the hub's list is the checklist). */
export function hubLocked(rungs: readonly Rung[], tier: Tier, done: (id: string) => boolean): boolean {
  return tier > 1 && (hubLocked(rungs, (tier - 1) as Tier, done) || hubMissing(rungs, tier, done).length > 0);
}

/** What a locked tier waits on, like "Finish the Warm up challenges (2 to go)"; null when it's open. */
export function hubNeeds(rungs: readonly Rung[], tier: Tier, done: (id: string) => boolean): string | null {
  if (!hubLocked(rungs, tier, done)) return null;
  // Behind a locked tier: that one's hub comes first.
  let below = (tier - 1) as Tier;
  while (below > 1 && hubLocked(rungs, below, done)) below = (below - 1) as Tier;
  const left = hubMissing(rungs, (below + 1) as Tier, done).length;
  return `Finish the ${TIER_NAMES[below - 1]} challenges (${left} to go)`;
}

/** Every challenge on the tier is done (a checked hub). */
export function tierDone(rungs: readonly Rung[], tier: Tier, done: (id: string) => boolean): boolean {
  const ids = tierGoals(rungs, tier);
  return ids.length > 0 && ids.every(done);
}

/** Tiers that are open now but weren't before `goalId` was done, for the "Tier 2 unlocked" callout. */
export function unlockedBy(rungs: readonly Rung[], goalId: string, done: (id: string) => boolean): Tier[] {
  const before = (id: string) => id !== goalId && done(id);
  return ([2, 3] as const).filter((t) => hubLocked(rungs, t, before) && !hubLocked(rungs, t, done));
}

/** "Tier 2: Getting good". */
export function hubName(tier: Tier): string {
  return `Tier ${tier}: ${TIER_NAMES[tier - 1]}`;
}

/** One row of a hub's list. */
export interface HubEntry {
  id: string;
  /** The goal: "High Score: 15,000". */
  text: string;
  /** A line on how to do it (`challengeHint`). */
  how: string;
  /** Stat points it pays the first time. */
  reward: number;
  done: boolean;
}

/** The hub list on screen, for React. */
export interface HubListHud {
  tier: Tier;
  /** "Tier 1: Warm up". */
  name: string;
  /** The highlighted row. */
  index: number;
  items: readonly HubEntry[];
}

/** A tier's challenges as the hub's list rows, in ladder order (only ones with a start spot on this map). */
export function hubEntries(
  rungs: readonly Rung[],
  tier: Tier,
  spots: readonly ChallengeSpot[],
  done: (id: string) => boolean,
): HubEntry[] {
  const byId = new Map(spots.map((s) => [s.id, s]));
  return rungs.flatMap((r) => {
    const s = r.tier === tier ? byId.get(r.id) : undefined;
    return s ? [{ id: s.id, text: s.text, how: challengeHint(s.id, s.kind), reward: r.statPoints, done: done(s.id) }] : [];
  });
}

/** What the hub list wants done. */
export type HubPick = { type: "pick"; id: string } | { type: "close" };

/**
 * The hub's list while it's open (the sim holds still): up/down moves (one
 * step per push), X picks, O or Start closes. Its `hud` is a new object only
 * when something on it changes.
 */
export class HubList {
  hud: HubListHud | null = null;
  private lastY = 0;

  get open(): boolean {
    return this.hud !== null;
  }

  /** Opens on the first challenge not done yet (or the top). */
  show(tier: Tier, items: readonly HubEntry[]): void {
    const index = Math.max(0, items.findIndex((i) => !i.done));
    this.hud = { tier, name: hubName(tier), index, items };
    this.lastY = 0;
  }

  hide(): void {
    this.hud = null;
  }

  /** Moves the highlight (a hover or tap). */
  setIndex(index: number): void {
    const h = this.hud;
    if (h && index !== h.index && index >= 0 && index < h.items.length) this.hud = { ...h, index };
  }

  handle(f: InputFrame): HubPick | null {
    const h = this.hud;
    if (!h) return null;
    const y = f.moveY > 0.5 ? 1 : f.moveY < -0.5 ? -1 : 0;
    if (y !== 0 && y !== this.lastY && h.items.length) this.setIndex((h.index - y + h.items.length) % h.items.length);
    this.lastY = y;
    if (f.pressed.grab || f.pressed.pause) return { type: "close" };
    const item = this.hud!.items[this.hud!.index];
    if (f.pressed.ollie && item) return { type: "pick", id: item.id };
    return null;
  }
}

/** Seconds of free skate the guide arrow to a newly unlocked hub shows for (#87). */
export const GUIDE_TIME = 12;

/** The guide arrow on the HUD. */
export interface HubGuideHud {
  tier: Tier;
  /** "Tier 2: Getting good". */
  name: string;
  /** Which way to go, in whole degrees from the camera's heading: 0 straight ahead, 90 right, -90 left, 180 behind. */
  angle: number;
  /** Whole meters to the hub. */
  meters: number;
}

/**
 * Which way `to` is from `from`, in degrees from the heading `face` (a
 * direction on the ground): 0 ahead, positive to the right, ±180 behind.
 * Right of a heading (x, z) is (-z, x), as on screen for a camera looking along it.
 */
export function guideAngle(from: { x: number; z: number }, face: { x: number; z: number }, to: { x: number; z: number }): number {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const ahead = dx * face.x + dz * face.z;
  const right = dx * -face.z + dz * face.x;
  return (Math.atan2(right, ahead) * 180) / Math.PI;
}

/**
 * The guide to a hub a goal just unlocked (#87): an arrow on the HUD that
 * points the way for `GUIDE_TIME` seconds of free skate, or until you roll
 * into the hub. Game only counts it down and shows it in free skate, so one
 * unlocked on a challenge's pass card waits until you're back skating.
 */
export class HubGuide {
  /** Seconds of free skate left. */
  left = GUIDE_TIME;
  private last: HubGuideHud | null = null;

  constructor(readonly hub: HubSpot) {}

  /** `dt` seconds of free skate with the skater at (x, z); false once it's done (time's up, or they reached the hub). */
  step(dt: number, x: number, z: number): boolean {
    this.left -= dt;
    return this.left > 0 && Math.hypot(this.hub.x - x, this.hub.z - z) > HUB_R;
  }

  /** The arrow for a skater at `at` seen by a camera heading `face`; the same object until the whole degrees or meters change. */
  hud(at: { x: number; z: number }, face: { x: number; z: number }): HubGuideHud {
    const angle = Math.round(guideAngle(at, face, this.hub)) || 0;
    const meters = Math.round(Math.hypot(this.hub.x - at.x, this.hub.z - at.z));
    const last = this.last;
    if (last && last.angle === angle && last.meters === meters) return last;
    return (this.last = { tier: this.hub.tier, name: hubName(this.hub.tier), angle, meters });
  }
}
